import React, { useMemo } from 'react';
import { IReference } from '../../schema/expressions/references/IReference';
import {
  AsyncServicesEngineContextProvider,
  IAsyncServicesEngineContextProviderProps,
} from '../../schema/expressions/repository/async/context/AsyncServicesEngineContext';
import { IRootWidget } from '../../schema/widgets/repository/containers/root/IRootWidget';
import { TreeReferencesContextProvider } from './TreeReferencesContext';

export interface IRootWidgetIdentifier {
  type: string;
  id: string;
  root: IRootWidget;
}

export interface ITreeReferencesGlobalContext {
  /** Tutte le possibili radici (es. sezioni) */
  allRoots: IRootWidgetIdentifier[];
  selectedRootId?: string | null; // La passiamo qui per comodità ma è il posto sbagliato
  /** Altri riferimenti */
  otherReferences: IReference[];
}

export const TreeReferencesGloablContext =
  React.createContext<ITreeReferencesGlobalContext | null>(null);

interface ITreeReferencesGlobalContextProviderProps {
  allRoots: IRootWidgetIdentifier[];
  selectedRootId?: string | null; // La passiamo qui per comodità ma è il posto sbagliato
  otherReferences: IReference[];
  children: React.ReactNode;

  /** Elenco dei WebService utilizzabili nelle espressioni */
  async: Omit<IAsyncServicesEngineContextProviderProps, 'children'>;
}

/**
 * Definisce il contesto globale di modifica delle espressioni, con informazioni
 * sul campione, le sezioni, ecc.
 */
export function TreeReferencesGlobalContextProvider(
  props: ITreeReferencesGlobalContextProviderProps,
) {
  const { allRoots, selectedRootId, otherReferences, async } = props;

  const value = useMemo(() => {
    return {
      allRoots,
      selectedRootId,
      otherReferences,
    };
  }, [allRoots, selectedRootId, otherReferences]);

  return (
    <TreeReferencesGloablContext.Provider value={value}>
      <AsyncServicesEngineContextProvider {...async}>
        <TreeReferencesContextProvider>
          {props.children}
        </TreeReferencesContextProvider>
      </AsyncServicesEngineContextProvider>
    </TreeReferencesGloablContext.Provider>
  );
}

/**
 * Ritorna il contesto di modifica delle espressioni padre. Il contesto
 * delle espressioni globale non fa riferimento a singoli Widget ma definisce
 * l'insieme delle variabili disponibili a livello root.
 */
export function useTreeReferencesGlobalContext(): ITreeReferencesGlobalContext | null {
  const context = React.useContext(TreeReferencesGloablContext);
  return context;
}
